import { alpha, Box, darken } from "@mui/material";
import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

export interface ILoadingBarRef {
  finish(): void;
  start(): void;
}

interface IProps {}

export const LoadingBar = forwardRef(
  (
    {}: IProps,
    ref: React.ForwardedRef<ILoadingBarRef>
  ): React.ReactElement<IProps> => {
    const [active, setActive] = useState(false);
    const barRef = useRef<HTMLDivElement>(null);
    const shouldAnimate = useRef(false);

    const startAnimation = () => {
      barRef.current?.classList.remove("withTransition");
      let width = 0;
      let speed = 1;

      function animate() {
        if (barRef.current && shouldAnimate.current) {
          width += speed;
          speed *= 0.98985;
          barRef.current!.style.width = width + "%";
          requestAnimationFrame(animate);
        }
      }

      animate();
    };

    useImperativeHandle(
      ref,
      () => ({
        start() {
          if (barRef.current) {
            barRef.current.style.width = "0%";
            shouldAnimate.current = true;
            setActive(true);
            startAnimation();
          }
        },
        finish() {
          if (barRef.current) {
            shouldAnimate.current = false;
            barRef.current.classList.add("withTransition");
            barRef.current.style.width = "100%";
            setActive(false);
          }
        },
      }),
      [barRef.current, active]
    );

    return (
      <Box
        sx={(theme) => ({
          position: "fixed",
          width: "100vw",
          height: "100vh",
          zIndex: 100000,
          background: alpha(darken(theme.palette.primary.main, 0.95), 0.2),
          opacity: active ? 1 : 0,
          visibility: active ? "visible" : "hidden",
          transition: theme.transitions.create(["opacity", "visibility"], {
            duration: 200,
            delay: active ? 0 : 200,
          }),
        })}
      >
        <Box
          ref={barRef}
          sx={(theme) => ({
            zIndex: 1,
            "&.withTransition": {
              transition: theme.transitions.create(["width"], {
                duration: 300,
              }),
            },
            position: "absolute",
            backgroundColor: alpha(theme.palette.primary.main, 0.825),
            top: 0,
            height: 3,
          })}
        />
      </Box>
    );
  }
);

export default LoadingBar;
